<script setup lang="ts">
import { _CREATE, _VIEW, _EDIT } from '@shell/config/query-params';
import { ref, computed, useTemplateRef, watch } from 'vue';
import { useStore } from 'vuex';
import semver from 'semver';
import { useI18n } from '@shell/composables/useI18n';
import Checkbox from '@components/Form/Checkbox/Checkbox.vue';
import { Banner } from '@components/Banner';
import IngressCards from '@shell/edit/provisioning.cattle.io.cluster/ingress/IngressCards.vue';
import {
  INGRESS_OPTIONS, INGRESS_DUAL, TRAEFIK, INGRESS_NGINX, INGRESS_NONE, INGRESS_MIGRATION_DOCS_LINK, INGRESS_CLASS_DEFAULT, INGRESS_CONTROLLER_CLASS_DEFAULT, INGRESS_CLASS_MIGRATION, INGRESS_CONTROLLER_CLASS_MIGRATION
} from '@shell/edit/provisioning.cattle.io.cluster/shared';
import IngressConfiguration from '@shell/edit/provisioning.cattle.io.cluster/ingress/IngressConfiguration.vue';
import YamlEditor, { EDITOR_MODES } from '@shell/components/YamlEditor';
import { set, get, mergeWithReplace } from '@shell/utils/object';
import { saferDump } from '@shell/utils/create-yaml';
import RichTranslation from '@shell/components/RichTranslation.vue';

interface Props {
  mode?: string;
  value: string | string[];
  nginxSupported: boolean;
  traefikSupported: boolean;
  nginxChart: string;
  traefikChart: string;
  userChartValues: any;
  versionInfo: any;
  originalIngressController?: string | string[];
  kubernetesVersion?: string;
  previousKubeVersion?: string;
}
const {
  mode = _CREATE,
  value,
  nginxChart,
  traefikChart,
  nginxSupported,
  traefikSupported,
  userChartValues,
  versionInfo,
  originalIngressController = INGRESS_NONE,
  kubernetesVersion,
  previousKubeVersion
} = defineProps<Props>();

const emit = defineEmits(['update:value', 'error', 'config-validation-changed', 'yaml-validation-changed', 'update-values']);
const store = useStore();
const { t } = useI18n(store);
const nginxYaml = useTemplateRef('nginx-yaml');
const traefikYaml = useTemplateRef('traefik-yaml');

const showAdvanced = ref<Boolean>(false);
const isView = computed(() => mode === _VIEW);
const isEdit = computed(() => mode === _EDIT);
const showTraefikBanner = ref<Boolean>(false);
const traefikMerged = ref(initYamlEditor(traefikChart));
const nginxMerged = ref(initYamlEditor(nginxChart));
const showConfig = computed(() => !!versionInfo[traefikChart] || !!versionInfo[nginxChart]);
const isIngressDisableVersion = computed(() => !!kubernetesVersion && semver.gte(kubernetesVersion, 'v1.37.0'));
const showTransitioningBanner = computed(() => traefikSupported && !isIngressDisableVersion.value);
const showNginxUpgradeBlocked = computed(() => isEdit.value && isIngressDisableVersion.value && ingressSelection.value === INGRESS_NGINX);
const originalUsedNginx = computed(() => originalIngressController === INGRESS_NGINX || Array.isArray(originalIngressController));
const crossesIngressDisableVersion = computed(() => isIngressDisableVersion.value && !!semver.valid(previousKubeVersion) && semver.lt(previousKubeVersion as string, 'v1.37.0'));
const showNginxUpgradeWarning = computed(() => isEdit.value && crossesIngressDisableVersion.value && originalUsedNginx.value && ingressSelection.value !== INGRESS_NGINX);
const showSelectedBanner = computed(() => (isEdit.value && ingressSelection.value !== TRAEFIK) || !!showTraefikBanner.value);

// in traefik v40 the nginx key changed from kubernetesIngressNginx to kubernetesIngressNGINX
const traefikNginxKey = computed(() => {
  const chartVersion = versionInfo[traefikChart]?.chart?.version;

  // regex: replace zeros that follow . and precede digits with just the .<digit> eg 32.1.002 -> 32.1.2
  // semver coerce will return null if version has leading zeros
  const cleaned = chartVersion?.replace(/\.0+(\d)/g, '.$1');
  const coerced = cleaned ? semver.coerce(cleaned) : null;

  if (coerced && semver.lt(coerced, '40.0.0')) {
    return 'kubernetesIngressNginx';
  }

  return 'kubernetesIngressNGINX';
});

watch(traefikNginxKey, (newKey, oldKey) => {
  if (oldKey && newKey !== oldKey) {
    const oldValues = get(traefikMerged.value, `providers.${ oldKey }`);

    if (oldValues) {
      set(traefikMerged.value, `providers.${ newKey }`, oldValues);
      delete (traefikMerged.value as any)?.providers?.[oldKey];

      // Only emit if the user had previously saved values under the old key
      // this will cover the scenario where a user is using 'dual mode' then upgrades k8s version to one that uses a newer traefik version
      const savedOldValues = get(userChartValues[traefikChart], `providers.${ oldKey }`);

      if (savedOldValues) {
        emit('update-values', traefikChart, traefikMerged.value);
        updateYaml(traefikYaml.value, traefikMerged.value);
      }
    }
  }
});

const ingressSelection = computed(() => {
  if (Array.isArray(value) ) {
    return INGRESS_DUAL;
  } else if (!value || value.length === 0) {
    return INGRESS_NONE;
  } else {
    return value;
  }
});
const ingressOptions = computed(() => {
  return INGRESS_OPTIONS.filter((option) => {
    if (option.id === INGRESS_NGINX) {
      return nginxSupported && !(mode === _CREATE && isIngressDisableVersion.value);
    } else if (option.id === TRAEFIK) {
      return traefikSupported;
    } else if (option.id === INGRESS_DUAL) {
      if (mode === _CREATE) {
        return false;
      } else {
        return traefikSupported && nginxSupported;
      }
    }

    return true;
  }).map((option) => {
    const nginxDisabled = option.id === INGRESS_NGINX && isIngressDisableVersion.value;

    return {
      ...option,
      content:  nginxDisabled && option.unsupportedContent ? option.unsupportedContent : option.content,
      doc:      option.doc,
      selected: option.id === ingressSelection.value,
      disabled: mode === _VIEW || nginxDisabled
    };
  });
});

const ingressEnabled = computed({
  get() {
    return ingressSelection.value !== INGRESS_NONE;
  },
  set(val) {
    if (!val) {
      emit('update:value', INGRESS_NONE);
    } else {
      if (traefikSupported) {
        emit('update:value', TRAEFIK);
      } else if (nginxSupported && !isIngressDisableVersion.value) {
        emit('update:value', INGRESS_NGINX);
      } else {
        emit('update:value', INGRESS_NONE);
      }
    }
  }
});

function initYamlEditor(chart: string) {
  const defaultChartValue = versionInfo[chart];

  return mergeWithReplace(defaultChartValue?.values, userChartValues[chart]);
}

function setCompatibilityModeValues(val: boolean) {
  const key = traefikNginxKey.value;

  set(traefikMerged.value, `providers.${ key }.enabled`, val);
  if (!val) {
    set(traefikMerged.value, `providers.${ key }.ingressClass`, INGRESS_CLASS_DEFAULT);
    set(traefikMerged.value, `providers.${ key }.controllerClass`, INGRESS_CONTROLLER_CLASS_DEFAULT);
  } else {
    set(traefikMerged.value, `providers.${ key }.ingressClass`, INGRESS_CLASS_MIGRATION);
    set(traefikMerged.value, `providers.${ key }.controllerClass`, INGRESS_CONTROLLER_CLASS_MIGRATION);
  }
}

function preconfigureTraefik() {
  set(traefikMerged.value, 'ports.web.hostPort', 8000);
  set(traefikMerged.value, 'ports.websecure.hostPort', 8443);
  setCompatibilityModeValues(true);
  emit('update-values', traefikChart, traefikMerged.value);
}

const nginxHttp = computed({
  get() {
    return get(nginxMerged.value, 'controller.hostPort.ports.http');
  },
  set(val: string) {
    set(nginxMerged.value, 'controller.hostPort.ports.http', Number(val));
    emit('update-values', nginxChart, nginxMerged.value);
    updateYaml(nginxYaml.value, nginxMerged.value);
  }
});
const nginxHttps = computed({
  get() {
    return get(nginxMerged.value, 'controller.hostPort.ports.https');
  },
  set(val: string) {
    set(nginxMerged.value, 'controller.hostPort.ports.https', Number(val));
    emit('update-values', nginxChart, nginxMerged.value);
    updateYaml(nginxYaml.value, nginxMerged.value);
  }
});
const traefikHttp = computed({
  get() {
    return get(traefikMerged.value, 'ports.web.hostPort');
  },
  set(val: string) {
    set(traefikMerged.value, 'ports.web.hostPort', Number(val));
    emit('update-values', traefikChart, traefikMerged.value);
    updateYaml(traefikYaml.value, traefikMerged.value);
  }
});
const traefikHttps = computed({
  get() {
    return get(traefikMerged.value, 'ports.websecure.hostPort');
  },
  set(val: string) {
    set(traefikMerged.value, 'ports.websecure.hostPort', Number(val));
    emit('update-values', traefikChart, traefikMerged.value);
    updateYaml(traefikYaml.value, traefikMerged.value);
  }
});

const compatibilityMode = computed({
  get() {
    return get(traefikMerged.value, `providers.${ traefikNginxKey.value }.enabled`);
  },
  set(val: boolean) {
    setCompatibilityModeValues(val);
    emit('update-values', traefikChart, traefikMerged.value);
    updateYaml(traefikYaml.value, traefikMerged.value);
  }
});

function selectIngress(id: string) {
  if (id === INGRESS_NGINX && isIngressDisableVersion.value) {
    return;
  }

  if ( id === INGRESS_DUAL) {
    const newValue: string | string[] = !Array.isArray(originalIngressController) ? (originalIngressController === TRAEFIK ? [TRAEFIK, INGRESS_NGINX] : [INGRESS_NGINX, TRAEFIK]) : originalIngressController;

    emit('update:value', newValue);

    preconfigureTraefik();
  } else {
    emit('update:value', id);
    if (id === TRAEFIK && !!isEdit.value) {
      showTraefikBanner.value = true;
    }
  }
}

function updateYaml(component: any, value: any) {
  if (component) {
    component.updateValue(saferDump(value));
  }
}

</script>
<template>
  <h3 class="mb-10">
    {{ t('cluster.ingress.title') }}
  </h3>
  <Checkbox
    v-model:value="ingressEnabled"
    :mode="mode"
    :label="t('cluster.ingress.enableIngress')"
  />
  <Banner
    v-if="showNginxUpgradeBlocked"
    color="error"
  >
    <RichTranslation k="cluster.ingress.banners.nginxUpgradeBlocked.label">
      <template #docsUrl="{ content }">
        <a
          :href="`${INGRESS_MIGRATION_DOCS_LINK}`"
          tabindex="0"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          {{ content }} <i class="icon icon-external-link" />
          <span class="sr-only">{{ t('generic.opensInNewTab') }}</span>
        </a>
      </template>
    </RichTranslation>
  </Banner>
  <Banner
    v-else-if="showNginxUpgradeWarning"
    color="warning"
    label-key="cluster.ingress.banners.nginxUpgradeWarning.label"
  />
  <div v-if="!ingressEnabled">
    <Banner
      color="warning"
      label-key="cluster.ingress.banners.disabled.label"
    />
  </div>
  <div v-else>
    <Banner
      v-if="showTransitioningBanner"
      color="info"
      label-key="cluster.ingress.banners.transitioning.label"
    />
    <IngressCards
      :options="ingressOptions"
      :mode="mode"
      :class="!showTransitioningBanner ? 'mt-10' : ''"
      @select="selectIngress"
    />
    <Banner
      v-if="showSelectedBanner"
      color="warning"
    >
      <RichTranslation :k="`cluster.ingress.banners.selected.${ingressSelection}.label`">
        <template #docsUrl="{ content }">
          <a
            :href="`${INGRESS_MIGRATION_DOCS_LINK}`"
            tabindex="0"
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            {{ content }} <i class="icon icon-external-link" />
            <span class="sr-only">{{ t('generic.opensInNewTab') }}</span>
          </a>
        </template>
      </RichTranslation>
    </Banner>
    <div v-if="showConfig">
      <div class="mt-20">
        <IngressConfiguration
          v-model:compatibility-mode="compatibilityMode"
          v-model:nginxHttp="nginxHttp"
          v-model:nginxHttps="nginxHttps"
          v-model:traefikHttp="traefikHttp"
          v-model:traefikHttps="traefikHttps"
          :mode="mode"
          :ingress-selection="ingressSelection"
          @validation-changed="emit('config-validation-changed', $event)"
        />
      </div>
      <div>
        <button
          type="button"
          class="btn role-link advanced-toggle mb-0"
          @click="showAdvanced = !showAdvanced"
        >
          {{ showAdvanced ? t('cluster.ingress.hideAdvanced') : t('cluster.ingress.showAdvanced') }}
        </button>
      </div>
      <template v-if="showAdvanced">
        <div class="row">
          <div
            v-if="ingressSelection === TRAEFIK || ingressSelection === INGRESS_DUAL"
            :class="{ 'col': true, 'span-6': ingressSelection === INGRESS_DUAL, 'span-12': ingressSelection !== INGRESS_DUAL }"
          >
            <p
              v-if="ingressSelection === INGRESS_DUAL"
              class="mb-10"
            >
              {{ t('cluster.ingress.traefik.header') }}
            </p>
            <YamlEditor
              ref="traefik-yaml"
              class="ingress-yaml-editor"
              data-testid="traefik-yaml-editor"
              :value="traefikMerged"
              :mode="mode"
              :scrolling="true"
              :as-object="true"
              :editor-mode="isView ? EDITOR_MODES.VIEW_CODE : EDITOR_MODES.EDIT_CODE"
              :hide-preview-buttons="true"
              @update:value="emit('update-values', traefikChart, $event)"
              @validationChanged="emit('yaml-validation-changed', {name: traefikChart, val: $event})"
            />
          </div>
          <div
            v-if="ingressSelection === INGRESS_NGINX || ingressSelection === INGRESS_DUAL"
            :class="{ 'col': true, 'span-6': ingressSelection === INGRESS_DUAL, 'span-12': ingressSelection !== INGRESS_DUAL }"
          >
            <p
              v-if="ingressSelection === INGRESS_DUAL"
              class="mb-10"
            >
              {{ t('cluster.ingress.nginx.header') }}
            </p>
            <YamlEditor
              ref="nginx-yaml"
              class="ingress-yaml-editor"
              data-testid="ingress-nginx-yaml-editor"
              :value="nginxMerged"
              :mode="mode"
              :scrolling="true"
              :as-object="true"
              :editor-mode="isView ? EDITOR_MODES.VIEW_CODE : EDITOR_MODES.EDIT_CODE"
              :hide-preview-buttons="true"
              @update:value="emit('update-values', nginxChart, $event)"
              @validationChanged="emit('yaml-validation-changed', {name: nginxChart, val: $event})"
            />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
.advanced-toggle {
  padding: 0;
  gap: 0;
  min-height: 20px;

  &:focus-visible {
    border-color: var(--primary);
    @include focus-outline;
    outline-offset: -2px;
  }
}

.ingress-yaml-editor {
  :deep(.cm-editor) {
    max-height: 600px;
  }
}
</style>
